import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  handlePhantomCallback,
  startPhantomSignMessage,
  phantomDeeplinkDebugSnapshot,
} from '../utils/phantomDeeplink';
import { useAuth } from '../contexts/AuthContext';
import { trackEvent } from '../utils/analytics';

/**
 * Route mounted at /auth/phantom-callback. Phantom's deeplink protocol
 * redirects back here twice during a sign-in:
 *   1. After the connect — surface a "Continue with Phantom" button.
 *      Tapping it gives Safari a fresh user-gesture, which it needs to
 *      actually open Phantom's universal link a second time.
 *   2. After the signMessage — decrypt, then complete login via
 *      AuthContext.loginWithSignedMessage.
 *
 * The page never auto-navigates away. Even on idle/error we keep the
 * card visible so the user (and we) can see what happened — silently
 * bouncing home was masking real failures.
 */
const PhantomCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { loginWithSignedMessage } = useAuth();
  const [status, setStatus] = useState<string>('Completing sign-in…');
  const [error, setError] = useState<string | null>(null);
  const [signNeededPubkey, setSignNeededPubkey] = useState<string | null>(null);
  const [resultKind, setResultKind] = useState<string>('(pending)');
  const [debugOpen, setDebugOpen] = useState(false);
  const [snapshot] = useState(() => phantomDeeplinkDebugSnapshot());
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const result = handlePhantomCallback(searchParams);
    setResultKind(result.kind);
    if (result.kind === 'idle') {
      setStatus('No sign-in in progress');
      setError('Phantom returned but we have no record of the flow on this device. Most likely localStorage was cleared between hops.');
      return;
    }
    if (result.kind === 'rejected') {
      // For an in-flight transaction, surface the cancel on the
      // originating page so the user sees it in the credits/swap UI.
      if (result.txReturnUrl) {
        const url = new URL(result.txReturnUrl, window.location.origin);
        url.searchParams.set('phantom-tx-pending', '1');
        window.location.replace(url.toString());
        return;
      }
      setError('Cancelled in Phantom');
      setStatus('Sign-in cancelled');
      return;
    }
    if (result.kind === 'error') {
      if (result.txReturnUrl) {
        const url = new URL(result.txReturnUrl, window.location.origin);
        url.searchParams.set('phantom-tx-pending', '1');
        window.location.replace(url.toString());
        return;
      }
      setError(result.message);
      setStatus('Sign-in failed');
      return;
    }
    if (result.kind === 'sign-needed') {
      setStatus('Connected to Phantom — sign a message to finish logging in');
      setSignNeededPubkey(result.publicKey);
      return;
    }
    if (result.kind === 'login-ready') {
      setStatus('Signing you in…');
      trackEvent('login_completed', { method: 'phantom_deeplink' });
      loginWithSignedMessage(result.publicKey, result.message, result.signature, 'phantom_deeplink')
        .then(() => {
          window.location.replace(result.returnUrl);
        })
        .catch((err) => {
          setError(err?.message || 'Login failed after Phantom sign');
          setStatus('Sign-in failed');
        });
      return;
    }
    if (result.kind === 'tx-signed') {
      // Signed tx is stashed in localStorage. Bounce back to the
      // originating page so its on-mount continuation can pick up,
      // submit to the network, and surface the result in context.
      setStatus('Sending transaction…');
      trackEvent('phantom_tx_signed', { operation: result.operation });
      const url = new URL(result.returnUrl, window.location.origin);
      url.searchParams.set('phantom-tx-pending', '1');
      window.location.replace(url.toString());
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const continueToPhantom = () => {
    if (!signNeededPubkey) return;
    setStatus('Opening Phantom…');
    const timestamp = new Date().toISOString();
    const message = `Sign in to deepnets.ai\nTimestamp: ${timestamp}`;
    const messageBytes = new TextEncoder().encode(message);
    try {
      startPhantomSignMessage(messageBytes, signNeededPubkey);
    } catch (err: any) {
      setError(err?.message || 'Could not start sign step');
      setStatus('Sign-in failed');
    }
  };

  const showSpinner = !error && !signNeededPubkey && resultKind !== 'idle';

  return (
    <div className="min-h-screen bg-crypto-dark text-white flex items-center justify-center px-4">
      <div className="bg-crypto-gray border border-gray-700 rounded-xl p-6 w-full max-w-sm shadow-2xl text-center">
        <div className="flex items-center justify-center mb-4">
          <div className="w-9 h-9 rounded-full bg-purple-500 flex items-center justify-center text-white font-bold mr-3">P</div>
          {showSpinner && (
            <div className="animate-spin w-5 h-5 border-2 border-crypto-accent border-t-transparent rounded-full" />
          )}
        </div>
        <p className="text-white font-medium">{status}</p>
        {error && <p className="text-red-400 text-xs mt-3">{error}</p>}
        {signNeededPubkey && !error && (
          <button
            onClick={continueToPhantom}
            className="mt-5 w-full py-3 bg-purple-500 hover:bg-purple-400 text-white font-semibold rounded-lg transition-colors"
          >
            Sign in →
          </button>
        )}
        {(error || resultKind === 'idle') && (
          <button
            onClick={() => navigate('/', { replace: true })}
            className="mt-4 w-full py-2 text-xs text-gray-400 hover:text-white"
          >
            Back to home
          </button>
        )}

        <button
          onClick={() => setDebugOpen(o => !o)}
          className="mt-6 text-[10px] text-gray-600 hover:text-gray-400"
        >
          {debugOpen ? 'Hide' : 'Show'} diagnostics
        </button>
        {debugOpen && (
          <pre className="mt-2 text-left text-[10px] text-gray-500 bg-black/40 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
{`result: ${resultKind}
${Object.entries(snapshot).map(([k, v]) => `${k}: ${v}`).join('\n')}
params: ${Array.from(searchParams.entries()).map(([k, v]) => `${k}=${v.slice(0, 24)}…`).join('\n        ') || '(none)'}`}
          </pre>
        )}
      </div>
    </div>
  );
};

export default PhantomCallback;
